import React from 'react';
import logoHorizontalUrl from '../../../assets/logo/spin-tenet-horizontal.svg';
import { useGetHealthQuery } from '@services/controlPlaneApi';

export interface GlobalHeaderProps {
  onHome: () => void;
  investigationName?: string;
  studyName?: string;
  scenarioName?: string;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  onHome,
  investigationName = 'Urban Wind Study',
  studyName = 'Downtown District',
  scenarioName = 'Baseline Scenario'
}) => {
  const { data: health, isSuccess, isError } = useGetHealthQuery(undefined, {
    pollingInterval: 10000,
  });

  const connectionStatus = isSuccess ? 'CONNECTED' : isError ? 'DISCONNECTED' : 'CONNECTING';

  return (
    <header className="ssw-header" role="banner">
      {/* Left: Brand Identity Lockup */}
      <div className="ssw-header-left">
        <div className="ssw-brand-lockup">
          <img
            src={logoHorizontalUrl}
            alt="SPIN TENET"
            className="ssw-brand-logo"
            draggable={false}
          />
          <div className="ssw-tag-group">
            <span className="ssw-header-divider" aria-hidden="true">/</span>
            <span className="ssw-surface-tag">SCIENTIFIC SIMULATION WORKSPACE</span>
          </div>
        </div>

        {/* Structural Context Breadcrumbs */}
        <div className="ssw-header-breadcrumbs" aria-label="Investigation Context">
          <span className="ssw-breadcrumb-divider" aria-hidden="true">|</span>
          <span className="ssw-breadcrumb-item">
            <span className="ssw-breadcrumb-key">Investigation:</span> {investigationName}
          </span>
          <span className="ssw-breadcrumb-divider" aria-hidden="true">/</span>
          <span className="ssw-breadcrumb-item">
            <span className="ssw-breadcrumb-key">Study:</span> {studyName}
          </span>
          <span className="ssw-breadcrumb-divider" aria-hidden="true">/</span>
          <span className="ssw-breadcrumb-item">
            <span className="ssw-breadcrumb-key">Scenario:</span> {scenarioName}
          </span>
        </div>
      </div>

      {/* Right: Server Status, AI Entry, Profile, Home Navigation Bridge */}
      <div className="ssw-header-right">
        {/* Remote Engine Server Connectivity Indicator */}
        <div
          className={`ssw-server-status ${connectionStatus.toLowerCase()}`}
          role="status"
          aria-live="polite"
          title={
            isSuccess
              ? `Remote Engine Server: CONNECTED\nHost: ${health?.hostname}\nPython: ${health?.python}\nSubstrate: ${health?.platform}\nRoot: ${health?.project_root}`
              : isError
              ? 'Remote Engine Server: DISCONNECTED'
              : 'Remote Engine Server: CONNECTING...'
          }
        >
          <span className="ssw-status-dot" aria-hidden="true" />
          <span className="ssw-status-label">
            Remote Engine Server: <strong>{connectionStatus}</strong>
          </span>
        </div>

        {/* AI Entry Point Placeholder Shell */}
        <div className="ssw-ai-shell" title="AI Copilot (Structural shell)">
          <svg className="ssw-ai-icon" viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M8 2L9.5 5.5L13 7L9.5 8.5L8 12L6.5 8.5L3 7L6.5 5.5L8 2Z" />
          </svg>
          <span className="ssw-ai-placeholder">Ask SPIN TENET...</span>
          <kbd className="ssw-ai-kbd">Ctrl K</kbd>
        </div>

        {/* System Status / Notification Shell */}
        <button
          type="button"
          className="ssw-header-icon-btn"
          aria-label="System status notifications (Structural shell)"
          title="System notifications (Shell placeholder)"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M4 6C4 3.79 5.79 2 8 2C10.21 2 12 3.79 12 6V9L13.5 11H2.5L4 9V6Z" />
            <path d="M6.5 13C6.5 13.83 7.17 14.5 8 14.5C8.83 14.5 9.5 13.83 9.5 13" />
          </svg>
          <span className="ssw-notification-pip" aria-hidden="true" />
        </button>

        {/* User Session Shell */}
        <div className="ssw-user-avatar-shell" title="Active user session (Structural shell)">
          <span>U</span>
        </div>

        {/* HOME: The singular return bridge back to Welcome Experience (Icon Only) */}
        <button
          type="button"
          id="ssw-home-btn"
          className="ssw-home-btn"
          onClick={onHome}
          aria-label="Home"
          title="Return to Welcome Experience"
        >
          <svg
            className="ssw-home-icon"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 9.5L10 3.5L17 9.5V17C17 17.55 16.55 18 16 18H4C3.45 18 3 17.55 3 17V9.5Z" />
            <path d="M7.5 18V11H12.5V18" />
          </svg>
          <span className="sr-only">Home</span>
        </button>
      </div>
    </header>
  );
};

export default GlobalHeader;
